import type { ReactNode } from 'react';
import { Logo } from '../atoms/Logo';
import authHeartHands from '../../assets/auth/heart-hands.png';
import authHeartHandsWebp from '../../assets/auth/heart-hands.webp';

export interface AuthPageLayoutProps {
  /** Accessible name for the white authentication panel. */
  panelLabel: string;
  heroTitle: string;
  heroSubtitle: string;
  children: ReactNode;
  logoAriaLabel?: string;
}


/** Shared Reed.ai authentication-page shell. Product auth behavior stays in the consuming app. */
export function AuthPageLayout({
  panelLabel,
  heroTitle,
  heroSubtitle,
  children,
  logoAriaLabel = 'Reed.ai',
}: AuthPageLayoutProps) {
  return (
    <main
      className="relative flex min-h-screen w-full items-center justify-center overflow-x-hidden bg-[#081351] bg-auth-radial px-4 py-8 sm:px-6 sm:py-10 xl:block"
    >
      <div
        className="pointer-events-none absolute min-h-dvh inset-0 left-[-25%] hidden overflow-hidden xl:flex flex-col items-center justify-end"
        aria-hidden="true"
      >
        <div className="z-10 absolute top-28 flex w-[375px] flex-col me-10 items-center gap-2 px-4 text-center text-white">
          <p className="font-raisonne text-[40px] font-semibold leading-[1.2]">{heroTitle}</p>
          <p className="font-raisonne text-[20px] font-semibold leading-[1.3]">{heroSubtitle}</p>
        </div>

        <picture className="flex w-full justify-center">
          <source srcSet={authHeartHandsWebp} type="image/webp" />
          <img
            src={authHeartHands}
            alt=""
            data-testid="hero-artwork"
            className="w-[1206px] max-w-[50%] select-none object-contain"
            draggable={false}
          />
        </picture>

      </div>

      <section
        aria-label={panelLabel}
        className={`relative z-20 flex w-full max-w-[375px] flex-col items-center overflow-hidden rounded-[16px] bg-white px-6 xl:absolute xl:right-[5.555556%] xl:-translate-y-1/2 gap-8 py-10 sm:py-16 xl:top-1/2`}
      >
        <Logo theme="dark" ariaLabel={logoAriaLabel} className="h-[45px] w-[120px]" />
        {children}
      </section>
    </main>
  );
}
